
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'

import {
    CalculationT,
    BillingAdditionalServicePrice,
    BillingUpdateCalculationPricePayload,
} from '@/core/types/billing.types'

import { billing } from '@/core/realisations/billing.realisation'

import useNotifications from '@/common/composable/useNotifications'

import useVuelidate from '@vuelidate/core'
import { required, decimal } from '@vuelidate/validators'

export default defineComponent({
    name: 'BillingUpdateCalculationPrice',
    components: {
        PageTitle,
        TheButton,
        Loader,
        TheInput,
    },
    setup() {
        const notifications = useNotifications()
        const route = useRoute()
        const router = useRouter()

        const calculation_id = Number(route.params.calculation_id)

        const state = reactive({
            is_loading: false,
            is_failed: false,
            is_submitting: false,
            calculation: null as CalculationT | null,
        })

        const form_state = reactive({
            deliveryCharge: null as string | number | null,
            insuranceCharge: null as string | number | null,
            orderCharge: null as string | number | null,
            packageCharge: null as string | number | null,
            unloadCharge: null as string | number | null,
        })

        const form_state$ = useVuelidate(
            {
                deliveryCharge: { required, decimal },
                insuranceCharge: { required, decimal },
                orderCharge: { required, decimal },
                packageCharge: { required, decimal },
                unloadCharge: { required, decimal },
            },
            form_state
        )

        const additional_services_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as BillingAdditionalServicePrice[],
        })

        const loadAdditionalServices = async () => {
            try {
                additional_services_state.is_loading = true

                const response = await billing.getAdditionalServicesPrices({
                    calculationId: calculation_id,
                })
                additional_services_state.list = response.data

                additional_services_state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении данных о доп. услугах произошла ошибка',
                })
                additional_services_state.is_failed = true
            } finally {
                additional_services_state.is_loading = false
            }
        }

        const loadCalculation = async () => {
            try {
                state.is_loading = true

                const response = await billing.getCalculation(calculation_id)
                state.calculation = response.data

                form_state.deliveryCharge = response.data.deliveryCharge
                form_state.insuranceCharge = response.data.insuranceCharge
                form_state.orderCharge = response.data.orderCharge
                form_state.packageCharge = response.data.packageCharge
                form_state.unloadCharge = response.data.unloadCharge

                loadAdditionalServices()

                state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении данных о ценах вычисления произошла ошибка',
                })
                state.is_failed = true
            } finally {
                state.is_loading = false
            }
        }

        const getAdditionalServiceName = (additional_service: BillingAdditionalServicePrice) => {
            switch (additional_service.additionalService.name) {
                case 'soft_package':
                    return 'Мягкая упаковка'
                case 'grid':
                    return 'Обрешетка'
                case 'loader':
                    return 'Грузчики'
                case 'pallet':
                    return 'Паллет'
                case 'hydraulic_trolley':
                    return 'Рохля'
                case 'manipulator':
                    return 'Манипулятор'
                case 'crane':
                    return 'Кран'
                default:
                    return additional_service.additionalService.name
            }
        }

        const onSubmit = async () => {
            if (state.is_submitting) return

            form_state$.value.$touch()
            if (form_state$.value.$invalid) return

            try {
                state.is_submitting = true

                const payload: BillingUpdateCalculationPricePayload = {
                    deliveryCharge: Number(form_state.deliveryCharge as string),
                    insuranceCharge: Number(form_state.insuranceCharge as string),
                    orderCharge: Number(form_state.orderCharge as string),
                    packageCharge: Number(form_state.packageCharge as string),
                    unloadCharge: Number(form_state.unloadCharge as string),
                    additionalServiceValues: additional_services_state.list.map((service) => {
                        return {
                            id: service.id,
                            cost: Number(service.cost),
                        }
                    }),
                }

                await billing.updateCalculationPrice(calculation_id, payload)

                notifications.notify({ title: 'Цена сохранена', type: 'success' })
                router.push('/billing')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При редактировании цены произошла ошибка',
                })
            } finally {
                state.is_submitting = false
            }
        }

        const getTitle = computed(
            () => `Редактирование цены вычисления ${state.calculation?.invoiceNumber || ''}`
        )

        //

        onBeforeMount(loadCalculation)

        //

        return {
            state,
            form_state,
            form_state$,
            additional_services_state,
            calculation_id,
            getTitle,
            loadCalculation,
            loadAdditionalServices,
            getAdditionalServiceName,
            onSubmit,
        }
    },
})
